import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { Box, Grid, Slider, Stack } from "@mui/material";
import VSNTypography from "../../../shared/VSNTypography";
import useOTBStyle from "../OTBStyle";
import PlotChartComponent from "./PlotChartComponent";

const CPET = ({ data, pdf }) => {
  const classes = useOTBStyle();

  const ageValues = data?.map((patient) => patient?.age);
  const numericValues = ageValues?.map(Number);
  const ageMax = Math?.max(...numericValues) ?? 0;
  const ageMin = Math?.min(...numericValues) ?? 0;
  const [ageData, setAgeData] = useState([ageMin, ageMax]);

  useEffect(() => {
    setAgeData([ageMin, ageMax]);
  }, [ageMin, ageMax]);

  const handleChange1 = (event, newValue) => {
    setAgeData(newValue);
  };

  const filteredData = data?.filter(
    (patient) => patient.age >= ageData[0] && patient.age <= ageData[1]
  );

  const FatRange1582 = [
    { label: "Meets Standard", min: 0, max: 35, color: "#C83C32" },
    {
      label: "Requires Fitness Program (No Op Restriction)",
      min: 36,
      max: 50,
      color: "#E8D0B1",
    },
    {
      label: "Restricted Duty (Requires Fitness Program)",
      min: 51,
      max: 100,
      color: "#7CAB53",
    },
  ];

  const getRangeCounts = (data, rangeData, valueKey) => {
    const counts = rangeData?.map(() => 0);

    data?.forEach((item) => {
      const value = item[valueKey];

      if (value !== null && value !== undefined) {
        const index = rangeData?.findIndex(
          (r) => value >= r.min && value <= r.max
        );
        if (index !== -1) {
          counts[index] += 1;
        }
      }
    });

    return counts;
  };

  const counts1582 = getRangeCounts(
    filteredData,
    FatRange1582,
    "fitness_percentile_1582"
  );
  const countsBodyFat = getRangeCounts(
    filteredData,
    FatRange1582,
    "body_fat_percentage"
  );

  const countsPeakVO2 = getRangeCounts(
    filteredData,
    FatRange1582,
    "peak_vo2_ml_kg_min"
  );

  const series = [
    {
      name: "Peak VO2 by Lean Mass",
      type: "bar",
      stack: "total",
      barWidth: "25%",
      itemStyle: { color: "#C3A577" },
      data: countsPeakVO2,
    },
    {
      name: "Body Fat %",
      type: "bar",
      stack: "total",
      barWidth: "25%",
      itemStyle: { color: "#E0BE94" },
      data: countsBodyFat,
    },
    {
      name: "Peak VO2 ml/kg/min",
      type: "bar",
      stack: "total",
      barWidth: "25%",
      itemStyle: { color: "#E8D0B1" },
      data: counts1582,
    },
  ];

  const option = {
    legend: {
      selectedMode: false,
      textStyle: {
        fontSize: 14,
        fontWeight: 600,
        color: "#000",
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: Math.max(...counts1582, ...countsBodyFat, ...countsPeakVO2) + 5,
      axisLine: {
        show: true,
        lineStyle: { color: "#6E7079", width: 1 },
      },
      nameTextStyle: {
        fontSize: 14,
        color: "#0A0A0B",
        fontWeight: 600,
      },
      axisLabel: {
        fontSize: 14,
        color: "#000",
        fontWeight: 600,
      },
    },
    xAxis: {
      type: "category",
      data: [
        "Meets Fitness Standard",
        "Requires Fitness Program",
        "Restricted Duty",
      ],
      nameTextStyle: {
        verticalAlign: "middle",
        fontSize: 14,
        color: "#0A0A0B",
        fontWeight: 600,
      },
      axisLabel: {
        formatter: (value) => {
          return value.split(" ").join("\n");
        },
        rotate: 0,
        interval: 0,
        fontSize: 14,
        color: "#000",
        fontWeight: 600,
      },
      axisLine: {
        show: true,
        lineStyle: { color: "#6E7079", width: 1 },
      },
    },
    series: series.map((s) => ({
      ...s,
      label: {
        show: false,
        position: "inside",
        color: "#000",
        fontSize: 12,
        fontWeight: "bold",
      },
      animation: false,
      animationDuration: 0,
      animationDurationUpdate: 0,
    })),
  };

  const fitnessRanges = [
    { label: "Meets Standard" },
    { label: "Requires Fitness Program (No Op Restriction)" },
    { label: "Restricted Duty (Requires Fitness Program)" },
  ];

  const pieChartData = fitnessRanges.map((range, index) => ({
    name: range.label,
    value: counts1582[index] || 0,
  }));

  const pieOption = {
    color: ["#A92823", "#EADBCB", "#A4B391"],
    title: {
      text: `{custom|${"Count of NFPA 1582"}}`,
      left: "center",
      textStyle: {
        rich: {
          custom: {
            fontSize: 16,
            fontWeight: 600,
            color: "#333",
          },
        },
      },
    },
    legend: {
      orient: "vertical",
      left: 0,
      bottom: 10,
      data: fitnessRanges?.map((range) => range.label),
      textStyle: {
        fontSize: 14,
        fontWeight: 600,
        color: "#000",
      },
    },
    toolbox: { show: true },
    series: [
      {
        name: "Count",
        type: "pie",
        radius: ["40%", "60%"],
        center: ["50%", "50%"],
        roseType: "area",
        itemStyle: { borderRadius: 8 },
        data: pieChartData,
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
      },
    ],
    label: {
      show: true,
      formatter: "{c}",
      textStyle: {
        fontWeight: "bold",
        fontSize: 14,
      },
    },
  };

  return (
    <Box>
      {pdf && (
        <Box
          className={classes.pdfReportLayout}
          sx={{ pageBreakAfter: "always" }}
        >
          <VSNTypography variant="h4">
            Cardiopulmonary Exercise Test (CPET)
          </VSNTypography>
          <Stack direction="row" gap={1}>
            <PlotChartComponent
              title="Age Group and Peak VO2ml/kg/min"
              data={filteredData}
              rangeData={FatRange1582}
              xTitle="Age Group"
              yTitle="VO2ml/kg/min"
              groupBy="age"
              valueKey="peak_vo2_ml_kg_min"
              width="340px"
              height="350px"
            />
            <PlotChartComponent
              title="Age Group and Body Fat%"
              data={filteredData}
              rangeData={FatRange1582}
              xTitle="Age Group"
              yTitle="Body Fat %"
              groupBy="age"
              valueKey="body_fat_percentage"
              width="320px"
              height="350px"
            />

            <PlotChartComponent
              title="Age Group and NFPA 1582 Fitness Percentile"
              data={filteredData}
              rangeData={FatRange1582}
              xTitle="Age Group"
              yTitle="1582 Fitness Percentile"
              groupBy="age"
              valueKey="fitness_percentile_1582"
              width="410px"
              height="350px"
              backColor
            />
          </Stack>

          <Stack direction="row" gap={2} mt={1}>
            <ReactECharts
              option={pieOption}
              style={{ height: "350px", width: "500px" }}
            />

            <ReactECharts
              option={option}
              style={{ height: "330px", width: "550px" }}
            />
          </Stack>
        </Box>
      )}

      {!pdf && (
        <Box>
          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Cardiopulmonary Exercise Test (CPET)
              </VSNTypography>
            </div>
            <Grid container flex={1} columnSpacing={2}>
              <Grid item xs={12} lg={6}>
                <PlotChartComponent
                  title="Age Group and Peak VO2ml/kg/min"
                  data={filteredData}
                  rangeData={FatRange1582}
                  xTitle="Age Group"
                  yTitle="VO2ml/kg/min"
                  groupBy="age"
                  valueKey="peak_vo2_ml_kg_min"
                  height="100%"
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <PlotChartComponent
                  title="Age Group and Body Fat%"
                  data={filteredData}
                  rangeData={FatRange1582}
                  xTitle="Age Group"
                  yTitle="Body Fat %"
                  groupBy="age"
                  valueKey="body_fat_percentage"
                  height="100%"
                  width="100%"
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                CPET vs NFPA 1582 Fitness Guidelines
              </VSNTypography>
            </div>
            <Grid container flex={1} columnSpacing={2}>
              <Grid item xs={12} lg={6}>
                <PlotChartComponent
                  title="Age Group and NFPA 1582 Fitness Percentile"
                  data={filteredData}
                  rangeData={FatRange1582}
                  xTitle="Age Group"
                  yTitle="1582 Fitness Percentile"
                  groupBy="age"
                  valueKey="fitness_percentile_1582"
                  height="100%"
                  width="100%"
                  backColor
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <ReactECharts
                  option={pieOption}
                  style={{ height: "100%", width: "100%" }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Cardiopulmonary Exercise Test (CPET)
              </VSNTypography>
            </div>

            <Stack direction="row" justifyContent="end">
              <Stack
                spacing={2}
                sx={{
                  padding: "24px 32px",
                  backgroundColor: "#F1E1CD",
                  // maxHeight: "150px",
                  borderRadius: "12px",
                }}
                p={4}
              >
                <Stack direction="row" spacing={4}>
                  <Box>
                    <VSNTypography
                      variant="h6"
                      sx={{
                        cursor: "pointer",
                        fontWeight: 700,
                        color: "#0A0A0B",
                        lineHeight: "31.5px",
                      }}
                    >
                      Age
                    </VSNTypography>
                  </Box>
                  <VSNTypography
                    variant="body2"
                    sx={{
                      cursor: "pointer",
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "#6E7079",
                      lineHeight: "23.44px",
                    }}
                  >
                    21 - 45
                  </VSNTypography>
                </Stack>
                <Box
                  sx={{
                    ".MuiSlider-track": {
                      backgroundColor: "#B29263",
                    },
                  }}
                >
                  <Slider
                    value={ageData}
                    onChange={handleChange1}
                    valueLabelDisplay="auto"
                    color="natural"
                    min={ageMin}
                    max={ageMax}
                  />
                </Box>
              </Stack>
            </Stack>

            <Box sx={{ flex: 1 }}>
              <ReactECharts
                option={option}
                style={{ height: 460, width: "100%" }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CPET;
